/**
 * Maestro Chat API Service
 * MAESTRO:P0D — Bloomberg Terminal-aesthetic chat interface
 *
 * Provides all communication with the MAESTRO AI chat endpoints.
 * Follows the fetchAPI pattern from api.js: credentials: 'include', CSRF token for mutations.
 */

import { fetchAPI } from './api';

/**
 * Maestro Chat API
 */
// ⛔ THE MAESTRO ROUTERS ARE MOUNTED UNDER /api/v1, NOT /api.
// fetchAPI prefixes API_BASE, which defaults to '/api', so `/maestro/...` resolves to
// /api/maestro/... -- a path the server has never served. Measured against staging:
//   GET /api/maestro/health     -> 404
//   GET /api/v1/maestro/health  -> 200
// streamChat below has always hardcoded '/api/v1/maestro/chat' and works, which is the proof
// the prefix was known and simply missing from its siblings.
//
// The ADMIN calls are corrected here because MaestroAdminPanel is routed and an admin hits
// them. The remaining /maestro/* calls in this file are left alone deliberately: MaestroChat is
// lazy-imported by App.jsx and never rendered (the /maestro/chat route serves MaestroHomepage),
// so they are unreachable, and whether that feature gets wired or deleted is an open decision.
// Fixing paths on a component nobody can reach would be unverifiable either way.
export const maestroApi = {
  /**
   * Send a message to the Maestro AI assistant.
   * Creates a new conversation if conversationId is null.
   *
   * @param {string} message - User message text (max 2000 chars)
   * @param {string|null} conversationId - Existing conversation UUID or null to start new
   * @param {Object} context - Optional context payload (e.g. active product, vault item)
   * @returns {Promise<{conversation_id, message_id, content, structured_data, suggested_followups, response_time_ms, intent_type}>}
   */
  sendMessage: async (message, conversationId = null, context = null) => {
    const body = { message };
    if (conversationId) body.conversation_id = conversationId;
    if (context) body.context = context;

    return fetchAPI('/maestro/chat', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  /**
   * Get paginated list of conversations for the current user.
   *
   * @param {number} limit - Results per page (default 20)
   * @param {number} offset - Pagination offset (default 0)
   * @returns {Promise<{conversations: Array, total: number, has_more: boolean}>}
   */
  getConversations: async (limit = 20, offset = 0) => {
    const params = new URLSearchParams();
    params.append('limit', limit);
    params.append('offset', offset);
    return fetchAPI(`/maestro/conversations?${params.toString()}`);
  },

  /**
   * Get a single conversation by ID.
   *
   * @param {string} id - Conversation UUID
   * @returns {Promise<{id, title, created_at, updated_at, message_count}>}
   */
  getConversation: async (id) => {
    return fetchAPI(`/maestro/conversations/${id}`);
  },

  /**
   * Get all messages within a conversation.
   *
   * @param {string} conversationId - Conversation UUID
   * @returns {Promise<{messages: Array}>}
   */
  getMessages: async (conversationId) => {
    return fetchAPI(`/maestro/conversations/${conversationId}/messages`);
  },

  /**
   * Archive (soft-delete) a conversation.
   *
   * @param {string} id - Conversation UUID
   * @returns {Promise<null>} 204 No Content
   */
  archiveConversation: async (id) => {
    return fetchAPI(`/maestro/conversations/${id}`, {
      method: 'DELETE',
    });
  },

  /**
   * Submit thumbs-up/down feedback on an assistant message.
   *
   * @param {string} messageId - Message UUID
   * @param {{ rating: 'up'|'down', comment?: string }} data
   * @returns {Promise<{feedback_id}>}
   */
  submitFeedback: async (messageId, data) => {
    return fetchAPI(`/maestro/messages/${messageId}/feedback`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  /**
   * Health-check the Maestro service.
   *
   * @returns {Promise<{status: 'ok'|'degraded', model, latency_ms}>}
   */
  checkHealth: async () => {
    return fetchAPI('/maestro/health');
  },

  // ── P1: SSE Streaming ────────────────────────────────────────────────────

  /**
   * Stream a chat response via SSE.
   *
   * @param {string} message - User message text
   * @param {string|null} conversationId - Existing conversation UUID or null
   * @param {function} onEvent - Callback for each SSE event: (eventType, data) => void
   * @param {AbortSignal} signal - AbortController signal for cancellation
   * @returns {Promise<void>}
   */
  streamChat: async (message, conversationId = null, onEvent, signal) => {
    const body = { message };
    if (conversationId) body.conversation_id = conversationId;

    try {
      const response = await fetch('/api/v1/maestro/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'text/event-stream',
        },
        credentials: 'include',
        body: JSON.stringify(body),
        signal,
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      let reading = true;
      while (reading) {
        const { done, value } = await reader.read();
        if (done) { reading = false; break; }

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n\n');
        buffer = lines.pop() || '';

        for (const chunk of lines) {
          const eventMatch = chunk.match(/^event: (\w+)\ndata: (.+)/s);
          if (eventMatch) {
            const eventType = eventMatch[1];
            try {
              const data = JSON.parse(eventMatch[2]);
              onEvent(eventType, data);
            } catch {
              // Skip malformed JSON
            }
          }
        }
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        throw err;
      }
    }
  },

  /**
   * Invalidate the current user's vault context cache.
   */
  invalidateVaultCache: async () => {
    return fetchAPI('/maestro/vault-cache/invalidate', {
      method: 'POST',
    });
  },

  // ── P1: Admin API ────────────────────────────────────────────────────────

  /**
   * Get admin conversation list (admin only).
   */
  getAdminConversations: async (page = 1, pageSize = 20) => {
    const params = new URLSearchParams({ page, page_size: pageSize });
    return fetchAPI(`/v1/maestro/admin/conversations?${params.toString()}`);
  },

  /**
   * Get admin conversation detail (admin only).
   */
  getAdminConversationDetail: async (conversationId) => {
    return fetchAPI(`/v1/maestro/admin/conversations/${conversationId}`);
  },

  /**
   * Get admin analytics for date range (admin only).
   */
  getAdminAnalytics: async (startDate, endDate) => {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    return fetchAPI(`/v1/maestro/admin/analytics?${params.toString()}`);
  },

  /**
   * Get admin real-time usage summary (admin only).
   */
  getAdminAnalyticsSummary: async () => {
    return fetchAPI('/v1/maestro/admin/analytics/summary');
  },
};

export default maestroApi;
